import { Props } from '@service-victoria/component-library/components/ConfirmationModal';
import React, { useCallback, useState } from 'react';
import { createGlobalStyle } from 'styled-components';

import Modal from 'Components/Modal';

import ModalContext, { ModalItem, ModalOptions } from './ModalContext';

type ModalContextProviderProps = {
  children: React.ReactChild;
};

const PreventScroll = createGlobalStyle`
  body {
      height: 100%;
      overflow: hidden;
  }
`;

type ModalRenderItem = Omit<ModalItem, 'onClose'> & {
  loader?: React.ReactChild;
};

export const ModalContextProvider: React.FC<ModalContextProviderProps> = ({ children }) => {
  const [isOpen, setOpen] = useState(false);
  const [content, setContent] = useState<ModalRenderItem | undefined>();
  const [actions, setActions] = useState<Props.Interfaces.Action[] | undefined>();
  const [canEscape, setCanEscape] = useState<boolean | undefined>();
  const [cancelOptions, setCancelOptions] = useState<{
    text?: string;
    onClick?: () => void;
  }>();

  const open = useCallback(
    (value: ModalItem | (() => Promise<ModalItem>), options?: ModalOptions) => {
      setOpen(true);
      if (typeof value === 'function') {
        if (options?.loader) {
          setContent({ loader: options.loader });
        }
        value().then(setContent);
        return;
      }

      if (options?.type === 'CONFIRMATION') {
        setActions(options.confirmationOptions?.actions || []);
        setCanEscape(options.confirmationOptions?.canEscape);
      } else {
        // clear out non-confirmation modal details
        setActions(undefined);
        setCanEscape(undefined);
      }

      setContent(value);
      setCancelOptions(() => options?.cancel);
    },
    []
  );

  const close = useCallback(() => {
    setOpen(false);
  }, []);

  return (
    <ModalContext.Provider
      value={{
        isOpen,
        open,
        close
      }}>
      {children}
      <Modal
        closeHandler={close}
        cancelHandler={cancelOptions?.onClick}
        cancelText={cancelOptions?.text}
        isOpen={isOpen}
        title={content?.title}
        canEscape={canEscape}
        actions={actions}>
        {content?.children || content?.loader}
      </Modal>
      {isOpen && <PreventScroll />}
    </ModalContext.Provider>
  );
};

export default ModalContextProvider;
